import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  GridToolbarContainer,
  GridToolbarQuickFilter,
  itIT,
} from "@mui/x-data-grid-pro";
import { PersistentDataGridPro as DataGridPro } from "../../components/DataGrid/PersistentDataGridPro";
import LinearProgress from "@mui/material/LinearProgress";
import { toast } from "react-toastify";
import api from "../../api/api";
import { CustomFooterPersonalized } from "../../components/DataGrid/CustomFooterPersonalized";
import { formatItalianNumber } from "../../utils/formatItalianNumber";

const fmtEuro = (v) =>
  v != null
    ? formatItalianNumber(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"
    : "-";

const fmtNumber = (v) =>
  v != null && v !== ""
    ? formatItalianNumber(v, { maximumFractionDigits: 2 })
    : "-";

const fmtPercent = (v) => (v == null ? "-" : `${formatItalianNumber(v, { maximumFractionDigits: 2 })}%`);

const Toolbar = () => (
  <GridToolbarContainer>
    <GridToolbarQuickFilter placeholder="Cerca..." />
  </GridToolbarContainer>
);

const Stat = ({ label, value }) => (
  <div className="border rounded-md bg-white px-3 py-2 min-h-[74px]">
    <div className="text-[11px] uppercase font-medium text-gray-500">{label}</div>
    <div className="text-xl font-semibold text-gray-900 mt-1">{value}</div>
  </div>
);

const GridBox = ({ title, rows, columns, height = 340 }) => (
  <div className="border rounded-md overflow-hidden bg-white">
    <div className="px-3 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-700">{title}</div>
    <div style={{ height }}>
      <DataGridPro
        density="compact"
        rows={rows || []}
        columns={columns}
        getRowId={(row) => row.id || row.key}
        localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
        slots={{ toolbar: Toolbar, footer: CustomFooterPersonalized, loadingOverlay: LinearProgress }}
        disableRowSelectionOnClick
      />
    </div>
  </div>
);

const baseColumns = [
  { field: "label", headerName: "Voce", flex: 2, minWidth: 180 },
  { field: "righe", headerName: "Righe", width: 80, align: "right", headerAlign: "right" },
  { field: "clienti", headerName: "Clienti", width: 90, align: "right", headerAlign: "right" },
  {
    field: "totale",
    headerName: "Totale",
    width: 120,
    align: "right",
    headerAlign: "right",
    valueFormatter: (p) => fmtEuro(p.value),
  },
];

const deltaColumns = [
  { field: "label", headerName: "Voce", flex: 2, minWidth: 180 },
  {
    field: "totale_da",
    headerName: "Da",
    width: 120,
    align: "right",
    headerAlign: "right",
    valueFormatter: (p) => fmtEuro(p.value),
  },
  {
    field: "totale_a",
    headerName: "A",
    width: 120,
    align: "right",
    headerAlign: "right",
    valueFormatter: (p) => fmtEuro(p.value),
  },
  {
    field: "delta",
    headerName: "Delta",
    width: 120,
    align: "right",
    headerAlign: "right",
    valueFormatter: (p) => fmtEuro(p.value),
  },
  {
    field: "delta_percentuale",
    headerName: "Delta %",
    width: 100,
    align: "right",
    headerAlign: "right",
    valueFormatter: (p) => fmtPercent(p.value),
  },
];

const detailColumns = [
  { field: "periodo", headerName: "Periodo report", width: 125 },
  { field: "periodo_competenza", headerName: "Competenza", width: 115 },
  { field: "periodo_fatturazione", headerName: "Fatturazione", width: 115 },
  { field: "periodicita_fatturazione", headerName: "Periodicita", width: 125 },
  { field: "cliente", headerName: "Cliente", flex: 1.6, minWidth: 220 },
  { field: "codice_cliente", headerName: "Cod. cliente", width: 115 },
  { field: "settore", headerName: "Settore", width: 125 },
  { field: "tipologia", headerName: "Tipo", width: 75, align: "center", headerAlign: "center" },
  { field: "particolarita_zucchetti", headerName: "Particolarità Z", width: 150 },
  { field: "referente", headerName: "Referente", width: 130 },
  { field: "codice", headerName: "Cod.", width: 85 },
  { field: "descrizione", headerName: "Descrizione", flex: 2, minWidth: 240 },
  { field: "quantita", headerName: "Qta", width: 80, align: "right", headerAlign: "right", valueFormatter: (p) => fmtNumber(p.value) },
  { field: "importo_unitario", headerName: "Unit.", width: 100, align: "right", headerAlign: "right", valueFormatter: (p) => fmtEuro(p.value) },
  { field: "totale", headerName: "Totale", width: 110, align: "right", headerAlign: "right", valueFormatter: (p) => fmtEuro(p.value) },
  { field: "stato", headerName: "Stato", width: 100 },
];

const particolaritaColumns = [
  { field: "periodo", headerName: "Periodo report", width: 125 },
  { field: "periodo_competenza", headerName: "Competenza", width: 115 },
  { field: "periodo_fatturazione", headerName: "Fatturazione", width: 115 },
  { field: "cliente", headerName: "Cliente", flex: 1.6, minWidth: 220 },
  { field: "codice_cliente", headerName: "Cod. cliente", width: 115 },
  { field: "particolarita", headerName: "Particolarità", width: 135 },
  { field: "codice", headerName: "Cod.", width: 85 },
  { field: "descrizione", headerName: "Descrizione", flex: 2, minWidth: 240 },
  { field: "importo_listino", headerName: "Listino", width: 100, align: "right", headerAlign: "right", valueFormatter: (p) => fmtEuro(p.value) },
  { field: "sconti_attivi", headerName: "Sconti attivi", width: 190 },
  { field: "totale", headerName: "Totale", width: 110, align: "right", headerAlign: "right", valueFormatter: (p) => fmtEuro(p.value) },
];

const VIEWS = [
  { id: "riepilogo", name: "Riepilogo" },
  { id: "particolarita", name: "Particolarità Z" },
  { id: "scostamenti", name: "Scostamenti" },
  { id: "dettaglio", name: "Dettaglio" },
  { id: "mie", name: "Miei inserimenti" },
];

export const ReportSezione3 = ({ data }) => {
  const [from, setFrom] = useState(data);
  const [to, setTo] = useState(data);
  const [mode, setMode] = useState("mese");
  const [view, setView] = useState("riepilogo");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFrom(data);
    setTo(data);
  }, [data]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/billing/altri-adempimenti/report", { params: { from, to, mode } });
      setReport(res.data || null);
    } catch (err) {
      setReport(null);
      toast.error(err?.response?.data?.message || "Errore caricamento report");
    } finally {
      setLoading(false);
    }
  }, [from, to, mode]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const totals = report?.totali || {};
  const aggregati = report?.aggregati || {};
  const scostamenti = report?.scostamenti || {};
  const modeLabel = mode === "competenza" ? "Fatturazione per competenza" : "Fatturazione per mese";
  const periodoDelta = scostamenti?.periodo_da && scostamenti?.periodo_a
    ? `${scostamenti.periodo_da} -> ${scostamenti.periodo_a}`
    : "seleziona almeno due mesi con dati";

  const periodoColumns = useMemo(() => [
    { field: "label", headerName: "Periodo", flex: 1, minWidth: 140 },
    { field: "righe", headerName: "Righe", width: 80, align: "right", headerAlign: "right" },
    {
      field: "totale",
      headerName: "Totale",
      width: 130,
      align: "right",
      headerAlign: "right",
      valueFormatter: (p) => fmtEuro(p.value),
    },
  ], []);

  return (
    <div className="el-container flex flex-col gap-3">
      <div className="grid grid-cols-1 md:grid-cols-[180px_180px_260px_auto] gap-3 items-end">
        <label className="flex flex-col text-xs font-medium uppercase text-gray-500">
          Da
          <input
            type="month"
            value={from || ""}
            onChange={(e) => setFrom(e.target.value)}
            className="mt-1 h-10 rounded-md border border-gray-300 px-3 text-base normal-case text-gray-900"
          />
        </label>
        <label className="flex flex-col text-xs font-medium uppercase text-gray-500">
          A
          <input
            type="month"
            value={to || ""}
            onChange={(e) => setTo(e.target.value)}
            className="mt-1 h-10 rounded-md border border-gray-300 px-3 text-base normal-case text-gray-900"
          />
        </label>
        <div className="flex h-10 rounded-md border border-gray-300 bg-white overflow-hidden">
          <button
            type="button"
            onClick={() => setMode("mese")}
            className={`flex-1 px-3 text-sm font-medium ${
              mode === "mese" ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            Per mese
          </button>
          <button
            type="button"
            onClick={() => setMode("competenza")}
            className={`flex-1 px-3 text-sm font-medium border-l ${
              mode === "competenza" ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            Per competenza
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {VIEWS.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setView(item.id)}
              className={`px-3 py-2 rounded-md text-sm font-medium border ${
                view === item.id
                  ? "bg-blue-50 border-blue-300 text-blue-700"
                  : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Stat label={modeLabel} value={fmtEuro(totals.totale || 0)} />
        <Stat label="Zucchetti" value={fmtEuro(totals.zucchetti || 0)} />
        <Stat label="iiPay" value={fmtEuro(totals.iipay || 0)} />
        <Stat label="Solo gruppo iiPay" value={fmtEuro(totals.gruppo_iipay || 0)} />
        <Stat label="Particolarità Z" value={fmtNumber(totals.particolarita_zucchetti || 0)} />
        <Stat label="Righe" value={fmtNumber(totals.righe || 0)} />
      </div>

      {loading && <LinearProgress />}

      {view === "riepilogo" && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <GridBox
            title={mode === "competenza" ? "Fatturato per mese di fatturazione" : "Fatturato per mese di competenza"}
            rows={aggregati.per_periodo || []}
            columns={periodoColumns}
          />
          <GridBox title="Zucchetti / iiPay" rows={aggregati.per_tipologia || []} columns={baseColumns} />
          <GridBox title="Per settore" rows={aggregati.per_settore || []} columns={baseColumns} />
          <GridBox title="Per referente" rows={aggregati.per_referente || []} columns={baseColumns} />
          <GridBox title="Per codice" rows={aggregati.per_codice || []} columns={baseColumns} />
          <GridBox title="Per cliente" rows={aggregati.per_cliente || []} columns={baseColumns} />
          <GridBox title="Per gruppo clienti" rows={aggregati.per_gruppo || []} columns={baseColumns} />
        </div>
      )}

      {view === "scostamenti" && (
        <div className="flex flex-col gap-3">
          <div className="text-xs text-gray-500">Confronto {periodoDelta}</div>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <GridBox title="Scostamento per codice" rows={scostamenti?.per_codice || []} columns={deltaColumns} />
            <GridBox title="Scostamento per cliente" rows={scostamenti?.per_cliente || []} columns={deltaColumns} />
            <GridBox title="Scostamento per settore" rows={scostamenti?.per_settore || []} columns={deltaColumns} />
            <GridBox title="Scostamento per referente" rows={scostamenti?.per_referente || []} columns={deltaColumns} />
            <GridBox title="Scostamento per gruppo" rows={scostamenti?.per_gruppo || []} columns={deltaColumns} />
          </div>
        </div>
      )}

      {view === "particolarita" && (
        <GridBox
          title="Particolarità Zucchetti: MMVN, CALVI, listini gratis, sconti"
          rows={report?.particolarita_zucchetti || []}
          columns={particolaritaColumns}
          height={620}
        />
      )}

      {view === "dettaglio" && (
        <GridBox title="Prestazioni inserite nel periodo" rows={report?.righe || []} columns={detailColumns} height={620} />
      )}

      {view === "mie" && (
        <GridBox title="Inserimenti fatti da me" rows={report?.mie_righe || []} columns={detailColumns} height={620} />
      )}
    </div>
  );
};
